/**
 * useCities.js
 *
 * Hook para obtener regiones y ciudades de Chile desde Backend Custom API
 * (que consulta MercadoLibre API con cache de 24h).
 *
 * Features:
 * - Fetch automático de regiones al montar componente
 * - Fetch de ciudades bajo demanda (cuando usuario selecciona región)
 * - Error handling robusto
 * - Loading states separados para regiones y ciudades
 *
 * ⚠️ ========== SECURITY WARNING ==========
 *
 * Este hook obtiene region_id y city_id desde Backend Custom.
 * Un usuario malicioso puede alterar form data para enviar IDs falsos.
 *
 * RIESGO:
 * - Usuario puede cambiar value del <select> vía DevTools
 * - Usuario puede enviar city_id inexistente en request
 *
 * VALIDACIÓN REQUERIDA EN BACKEND (CRÍTICO):
 * El Backend Custom DEBE validar city_id contra MercadoLibre API
 * antes de insertar en base de datos.
 *
 * Endpoints backend afectados:
 *   - POST /api/orders (destination_city_id)
 *   - POST /api/shipping/calculate-cost (destination_city_id)
 *   - POST /api/shipping/create-shipment (destination_city_id)
 *   - PUT /api/artist-profiles (shipping_address.city_id)
 *
 * @see frontend/creative-market/CLAUDE.md sección 3.5 (Security Guidelines)
 * @see backend/docs/refactor-framework/deliverables/strategies/STRATEGY-03-shopping-experience.md
 *
 * ⚠️ ========================================
 *
 * @example
 * ```javascript
 * function AddressForm() {
 *   const {
 *     regions,
 *     cities,
 *     loading_regions,
 *     loading_cities,
 *     fetchCities,
 *     error
 *   } = useCities();
 *
 *   const [selected_region, setSelectedRegion] = useState('');
 *
 *   const handleRegionChange = (region_id) => {
 *     setSelectedRegion(region_id);
 *     fetchCities(region_id); // Fetch cities when region changes
 *   };
 *
 *   return (
 *     <>
 *       <select value={selected_region} onChange={(e) => handleRegionChange(e.target.value)}>
 *         <option value="">Seleccionar región</option>
 *         {regions.map(region => (
 *           <option key={region.id} value={region.id}>
 *             {region.name}
 *           </option>
 *         ))}
 *       </select>
 *
 *       <select disabled={!selected_region || loading_cities}>
 *         <option value="">Seleccionar ciudad</option>
 *         {cities.map(city => (
 *           <option key={city.id} value={city.id}>
 *             {city.name}
 *           </option>
 *         ))}
 *       </select>
 *     </>
 *   );
 * }
 * ```
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '@/utils/apiClient';

/**
 * Hook para obtener regiones y ciudades de Chile desde Backend Custom
 *
 * @returns {Object} Hook state and functions
 * @returns {Array} returns.regions - Array de regiones con { id, name }
 * @returns {Array} returns.cities - Array de ciudades de la región seleccionada con { id, name }
 * @returns {boolean} returns.loading_regions - Estado de carga de regiones
 * @returns {boolean} returns.loading_cities - Estado de carga de ciudades
 * @returns {string|null} returns.error - Mensaje de error si ocurrió alguno
 * @returns {Function} returns.fetchCities - Función para cargar ciudades de una región
 * @returns {Function} returns.clearCities - Función para limpiar ciudades cargadas
 * @returns {Function} returns.clearError - Función para limpiar error
 */
export function useCities() {
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading_regions, setLoadingRegions] = useState(false);
  const [loading_cities, setLoadingCities] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Prevent double fetch in React StrictMode (development)
  const has_fetched_regions = useRef(false);

  /**
   * Fetch all regions from Backend Custom
   * Automatically called on component mount
   */
  const fetchRegions = async () => {
    setLoadingRegions(true);
    setError(null);

    try {
      const { success, data, error: api_error } = await apiClient.get('/api/shipping/regions');

      if (api_error || !success) {
        setError('No se pudieron cargar las regiones. Intenta nuevamente.');
        setRegions([]);
        return;
      }

      if (data && Array.isArray(data.regions)) {
        setRegions(data.regions);
      } else {
        setError('Respuesta inválida del servidor.');
        setRegions([]);
      }
    } catch (err) {
      setError('Error de conexión. Verifica tu internet.');
      setRegions([]);
    } finally {
      setLoadingRegions(false);
    }
  };

  /**
   * Fetch cities for a specific region
   *
   * @param {string} region_id - MercadoLibre region ID (e.g., "TUxDUk0")
   *
   * @example
   * fetchCities('TUxDUk0'); // Fetch cities for Región Metropolitana
   */
  // ✅ Memoize with useCallback to prevent function recreation (prevents useEffect loops)
  const fetchCities = useCallback(async (region_id) => {
    if (!region_id) {
      setCities([]);
      return;
    }

    setLoadingCities(true);
    setError(null);

    try {
      const { success, data, error: api_error } = await apiClient.get(
        `/api/shipping/regions/${region_id}/cities`
      );

      if (api_error || !success) {
        // Backend puede retornar 404 si región no existe
        if (api_error?.includes('404') || api_error?.includes('not found')) {
          setError('Región no encontrada.');
        } else {
          setError('No se pudieron cargar las ciudades. Intenta nuevamente.');
        }
        setCities([]);
        return;
      }

      if (data && Array.isArray(data.cities)) {
        setCities(data.cities);
      } else {
        setError('Respuesta inválida del servidor.');
        setCities([]);
      }
    } catch (err) {
      setError('Error de conexión. Verifica tu internet.');
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  }, []); // No dependencies - function is stable

  /**
   * Clear cities (useful when region selection changes)
   */
  const clearCities = () => {
    setCities([]);
  };

  /**
   * Clear error state
   */
  const clearError = () => {
    setError(null);
  };

  // Auto-fetch regions on mount
  // ✅ Use ref to prevent double fetch in React StrictMode (development)
  useEffect(() => {
    if (has_fetched_regions.current) {
      return; // Already fetched, skip
    }

    has_fetched_regions.current = true;
    fetchRegions();
  }, []);

  return {
    regions,
    cities,
    loading_regions,
    loading_cities,
    error,
    fetchCities,
    clearCities,
    clearError,
  };
}
